import { endOfToday, format, isToday, isTomorrow, isYesterday } from "date-fns";
import type {
  MeetingAction,
  MeetingActionType,
  MeetingAttendee,
  MeetingBoardGroupBy,
  MeetingRowData,
  MeetingTimeGroup,
} from "./meetingsTypes";

export type ObjectSearchMeetingRecord = {
  id: string;
  fields?: Record<string, unknown>;
  related?: Record<string, Array<Record<string, unknown>>>;
};

const TITLE_KEYS = ["title", "name", "subject", "native_title"];
const DATE_KEYS = [
  "scheduled_at",
  "start_time",
  "start_at",
  "meeting_start",
  "meeting_date",
  "native_start_time_tz",
  "created_at",
];
const COMPANY_NAME_KEYS = [
  "account_name",
  "company_name",
  "company",
  "organization",
  "org_name",
];
const COMPANY_DOMAIN_KEYS = ["company_domain", "domain", "website"];
const ACTION_KEYS = ["ai_action", "action", "next_action", "action_status", "status"];
const NOTES_KEYS = ["notes", "summary", "description", "meeting_notes"];
const ATTENDEE_KEYS = [
  "attendees",
  "participant_names",
  "participants",
  "attendee_names",
  "participant_emails",
  "native_participants_emails",
];

const normalizeWhitespace = (value: string) => value.replace(/\s+/g, " ").trim();

const asNonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const normalized = normalizeWhitespace(value);
  return normalized || undefined;
};

const asBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value > 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "yes", "1"].includes(normalized)) return true;
    if (["false", "no", "0"].includes(normalized)) return false;
  }
  return undefined;
};

const asDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  if (typeof value === "number") {
    const millis = value > 1_000_000_000_000 ? value : value * 1000;
    const date = new Date(millis);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === "string") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
};

const firstStringFromFields = (fields: Record<string, unknown>, keys: readonly string[]): string | undefined => (
  keys
    .map((key) => asNonEmptyString(fields[key]))
    .find((value): value is string => typeof value === "string")
);

const firstDateFromFields = (fields: Record<string, unknown>, keys: readonly string[]): Date | null => (
  keys
    .map((key) => asDate(fields[key]))
    .find((value): value is Date => value instanceof Date)
    || null
);

const firstRecord = (value: unknown): Record<string, unknown> | null => {
  if (!Array.isArray(value) || value.length === 0) return null;
  return asRecord(value[0]);
};

const toInitials = (name: string) => {
  const tokens = name
    .split(" ")
    .map((token) => token.trim())
    .filter(Boolean);

  if (tokens.length === 0) return "?";
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  return `${tokens[0][0] || ""}${tokens[tokens.length - 1][0] || ""}`.toUpperCase();
};

const extractNameFromObject = (value: Record<string, unknown>): string | undefined => {
  const namedValue =
    asNonEmptyString(value.name)
    || asNonEmptyString(value.display_name)
    || asNonEmptyString(value.full_name)
    || asNonEmptyString(value.email)
    || asNonEmptyString(value.user_name)
    || asNonEmptyString(value.title);

  if (namedValue) return namedValue;

  const firstName = asNonEmptyString(value.first_name);
  const lastName = asNonEmptyString(value.last_name);
  if (firstName || lastName) return normalizeWhitespace(`${firstName || ""} ${lastName || ""}`);

  return undefined;
};

const cleanAttendeeToken = (value: string): string => (
  value
    .replace(/^[\s"'[\]]+/, "")
    .replace(/[\s"'[\]]+$/, "")
    .trim()
);

const extractAttendeeTokens = (candidate: string): string[] => {
  const raw = candidate.trim();
  if (!raw) return [];

  if (raw.startsWith("[") && raw.endsWith("]")) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => (typeof item === "string" ? cleanAttendeeToken(item) : ""))
          .filter((item): item is string => Boolean(item));
      }
    } catch {
      // Fall through to delimiter parsing.
    }
  }

  return raw
    .split(",")
    .map((value) => cleanAttendeeToken(value))
    .filter((item): item is string => Boolean(item));
};

const extractAttendees = (record: ObjectSearchMeetingRecord): MeetingAttendee[] => {
  const names: string[] = [];
  const fields = record.fields || {};

  ATTENDEE_KEYS.forEach((key) => {
    const candidate = fields[key];

    if (typeof candidate === "string") {
      extractAttendeeTokens(candidate).forEach((name) => {
        const normalized = asNonEmptyString(name);
        if (normalized) names.push(normalized);
      });
      return;
    }

    if (!Array.isArray(candidate)) return;

    candidate.forEach((item) => {
      if (typeof item === "string") {
        const name = asNonEmptyString(item);
        if (name) names.push(name);
        return;
      }

      const itemRecord = asRecord(item);
      if (!itemRecord) return;
      const name = extractNameFromObject(itemRecord);
      if (name) names.push(name);
    });
  });

  const relatedUsers = record.related?.users;
  if (Array.isArray(relatedUsers)) {
    relatedUsers.forEach((user) => {
      const name = extractNameFromObject(user);
      if (name) names.push(name);
    });
  }

  const uniqueNames = Array.from(new Set(names.map((name) => normalizeWhitespace(name).toLowerCase())))
    .map((normalized) => names.find((candidate) => normalizeWhitespace(candidate).toLowerCase() === normalized))
    .filter((value): value is string => typeof value === "string");

  return uniqueNames.map((name) => ({
    name,
    initials: toInitials(name),
  }));
};

const deriveAction = (fields: Record<string, unknown>, when: Date | null): MeetingAction | null => {
  const rawAction = firstStringFromFields(fields, ACTION_KEYS)?.toLowerCase() || "";

  const actionType: MeetingActionType | null = (() => {
    if (rawAction.includes("followed")) return "followed_up";
    if (rawAction.includes("follow")) return "follow_up";
    if (rawAction.includes("prep")) return "prep";

    if (when && when.getTime() > Date.now()) return "prep";
    if (when && when.getTime() <= Date.now()) return "follow_up";
    return null;
  })();

  if (!actionType) return null;

  const labels: Record<MeetingActionType, string> = {
    prep: "Prep for meeting",
    follow_up: "Follow up",
    followed_up: "Followed up",
  };

  return {
    type: actionType,
    label: labels[actionType],
  };
};

const deriveCompany = (record: ObjectSearchMeetingRecord) => {
  const fields = record.fields || {};
  const account = firstRecord(record.related?.accounts);

  const relatedName = account ? extractNameFromObject(account) : undefined;
  const relatedDomain = account
    ? asNonEmptyString(account.domain)
    : undefined;

  const name = relatedName || firstStringFromFields(fields, COMPANY_NAME_KEYS);
  if (!name) return null;

  const domain = relatedDomain || firstStringFromFields(fields, COMPANY_DOMAIN_KEYS);

  return {
    name,
    ...(domain ? { domain } : {}),
  };
};

export const deriveMeetingGroup = (when: Date | null): MeetingTimeGroup => {
  if (!when) return "Earlier";
  if (isToday(when)) return "Today";
  if (isYesterday(when)) return "Yesterday";
  if (when.getTime() > endOfToday().getTime()) return "Coming up";
  return "Earlier";
};

const formatMeetingTime = (when: Date | null, group: MeetingTimeGroup) => {
  if (!when) return "No time";

  if (group === "Today" || group === "Yesterday") {
    return format(when, "h:mm a");
  }

  if (isTomorrow(when)) {
    return `Tomorrow, ${format(when, "h:mm a")}`;
  }

  if (group === "Coming up") {
    return format(when, "EEE, MMM d");
  }

  return format(when, "MMM d");
};

export const parseMeetingRecords = (records: ObjectSearchMeetingRecord[]): MeetingRowData[] => {
  const parsed = records.map<MeetingRowData>((record) => {
    const fields = record.fields || {};
    const when = firstDateFromFields(fields, DATE_KEYS);
    const group = deriveMeetingGroup(when);
    const title = firstStringFromFields(fields, TITLE_KEYS) || "Untitled meeting";
    const company = deriveCompany(record);
    const action = deriveAction(fields, when);
    const attendees = extractAttendees(record);

    const hasRecording = Boolean(
      asBoolean(fields.has_recording)
      ?? asBoolean(fields.recording_available)
      ?? asNonEmptyString(fields.recording_url)
      ?? asNonEmptyString(fields.transcript_url),
    );

    const hasNotes = Boolean(
      asBoolean(fields.has_notes)
      ?? asBoolean(fields.notes_available)
      ?? firstStringFromFields(fields, NOTES_KEYS),
    );

    const timeLabel = formatMeetingTime(when, group);

    const searchableText = [
      title,
      company?.name || "",
      attendees.map((attendee) => attendee.name).join(" "),
      action?.label || "",
      timeLabel,
    ].join(" ").toLowerCase();

    return {
      id: record.id,
      title,
      timeLabel,
      group,
      company,
      action,
      attendees,
      hasRecording,
      hasNotes,
      when,
      searchableText,
    };
  });

  return parsed.sort((left, right) => {
    if (!left.when && !right.when) return left.title.localeCompare(right.title);
    if (!left.when) return 1;
    if (!right.when) return -1;
    return right.when.getTime() - left.when.getTime();
  });
};

export const meetingBoardGroupValue = (meeting: MeetingRowData, groupBy: MeetingBoardGroupBy) => {
  if (groupBy === "time_group") return meeting.group;

  if (groupBy === "action") {
    if (!meeting.action) return "No action";
    if (meeting.action.type === "prep") return "Prep";
    if (meeting.action.type === "follow_up") return "Follow up";
    return "Followed up";
  }

  return meeting.company ? "With company" : "Internal";
};

const PREP_SECTION_HEADINGS = new Set([
  "executive summary",
  "risks and blockers",
  "opportunities and expansion angles",
  "stakeholders and relationship notes",
  "open tasks / follow-ups",
  "suggested talking points and specific questions for this meeting",
]);

const stripMarkdown = (line: string): string => (
  line
    .replace(/^\s*#{1,6}\s+/, "")
    .replace(/^\s*[-*+]\s+/, "")
    .replace(/^\s*\d+[.)]\s+/, "")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1")
    .replace(/[*_`]/g, "")
    .trim()
);

export const extractPrepBriefHighlights = (
  prepBrief: string | null | undefined,
  limit = 3,
): string[] => {
  if (!prepBrief || typeof prepBrief !== "string") return [];

  const highlights: string[] = [];
  const seen = new Set<string>();

  prepBrief
    .split(/\r?\n/)
    .map((line) => stripMarkdown(line))
    .forEach((line) => {
      if (!line || highlights.length >= limit) return;

      const normalized = line.replace(/:$/, "").trim().toLowerCase();
      if (PREP_SECTION_HEADINGS.has(normalized)) return;
      if (line.endsWith(":") && line.length <= 60) return;

      const dedupeKey = line.toLowerCase();
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      highlights.push(line);
    });

  return highlights;
};
